import _get from 'lodash/get'
import { P, match } from 'ts-pattern'
import { z } from 'zod'

import type { TextContent } from '@flows/core/i18n/types'
import type { AnyStepConfiguration, FlowValues } from '@flows/core/types/stepTypes'
import { SubFlow } from '@flows/insurance/types/subFlowTypes'

import { type ChildSavingStepParams, quoteSchema } from './types'

type Extension = {
  key: string
  icon: string
  title: TextContent
  text: TextContent
}

type InfoItem = {
  key: string
  icon: string
  text: TextContent
}

type InfoSection = {
  key: string
  title: TextContent
  items: InfoItem[]
}

const conditionSchema = z.object({
  flows: z.object({
    core: z.object({
      configs: z.object({
        productFlow: z.object({
          values: z.object({
            phone_number: z.string(),
            helperValues: z
              .object({
                shouldConfirmPhoneNumber: z.literal(false).optional(),
              })
              .optional(),
          }),
        }),
      }),
    }),
  }),
})

export const quote = (params: ChildSavingStepParams & { platform: 'web' | 'native' }) => {
  const schema = quoteSchema
  return {
    schema,
    stepContent: [
      {
        type: 'customComponent',
        name: 'QuoteMGA',
        props: {
          priceParamPath: params.helperFunctions.getHelperPath('quote_price'),
          headerTitle: {
            type: 'translatableText',
            translationKey: 'sharedFlows_childSaving:steps.quote.header.title',
          },
          headerSubtitle: {
            type: 'translatableText',
            translationKey: 'sharedFlows_childSaving:steps.quote.header.subtitle',
          },
          priceTitleTop: {
            type: 'translatableText',
            translationKey: 'sharedFlows_childSaving:steps.quote.price.titleTop',
          },
          priceTitleBottom: {
            type: 'translatableText',
            translationKey: 'sharedFlows_childSaving:steps.quote.price.titleBottom',
          },
          extensions: {
            title: {
              type: 'translatableText',
              translationKey: 'sharedFlows_childSaving:steps.quote.extensions.title',
            },
            items: [
              {
                key: 'supplementaryHealthInsurance',
                icon: 'clipboardWithCross_24',
                title: {
                  type: 'translatableText',
                  translationKey:
                    'sharedFlows_childSaving:steps.quote.extensions.supplementary.title',
                },
                text: {
                  type: 'translatableText',
                  translationKey:
                    'sharedFlows_childSaving:steps.quote.extensions.supplementary.text',
                },
              },
              {
                key: 'incomeProtection',
                icon: 'heart_24',
                title: {
                  type: 'translatableText',
                  translationKey:
                    'sharedFlows_childSaving:steps.quote.extensions.incomeProtection.title',
                },
                text: {
                  type: 'translatableText',
                  translationKey:
                    'sharedFlows_childSaving:steps.quote.extensions.incomeProtection.text',
                },
              },
              {
                key: 'paymentProtection',
                icon: 'family_24',
                title: {
                  type: 'translatableText',
                  translationKey:
                    'sharedFlows_childSaving:steps.quote.extensions.paymentProtection.title',
                },
                text: {
                  type: 'translatableText',
                  translationKey:
                    'sharedFlows_childSaving:steps.quote.extensions.paymentProtection.text',
                },
              },
            ] as const satisfies Extension[],
          },
          infoSections: [
            {
              key: 'benefits',
              title: {
                type: 'translatableText',
                translationKey: 'sharedFlows_childSaving:steps.quote.infoSections.benefits.title',
              },
              items: [
                {
                  key: 'item1',
                  icon: 'circleCheckmark_24',
                  text: {
                    type: 'translatableText',
                    translationKey:
                      'sharedFlows_childSaving:steps.quote.infoSections.benefits.item1.title',
                  },
                },
                {
                  key: 'item2',
                  icon: 'circleCheckmark_24',
                  text: {
                    type: 'translatableText',
                    translationKey:
                      'sharedFlows_childSaving:steps.quote.infoSections.benefits.item2.title',
                  },
                },
                {
                  key: 'item3',
                  icon: 'circleCheckmark_24',
                  text: {
                    type: 'translatableText',
                    translationKey:
                      'sharedFlows_childSaving:steps.quote.infoSections.benefits.item3.title',
                  },
                },
              ] as const satisfies InfoItem[],
            },
          ] as const satisfies InfoSection[],
        },
        inputs: [],
        outputs: [],
      },
    ],
    onPrepare: {
      type: 'simpleHook',
      callback: (flowValues: FlowValues) => {
        const amountOfMoney = _get(
          flowValues,
          params.helperFunctions.getParamPath('amount_of_money')
        )
        const price = match(amountOfMoney)
          .with('less_than_50', () => 25)
          .with('100', () => 100)
          .with(P.union('200', 'more_than_200'), () => 200)
          .otherwise(() => 0)

        return {
          formValues: {
            [params.helperFunctions.getHelperPath('quote_price')]: price,
          },
        }
      },
    },
    onSubmit: {
      type: 'dispatchedHook',
      callbackConfig: {
        name: 'updateConsultationRequest',
        inputs: [],
        outputs: [],
      },
    },
    nextStep: 'progress_last_step',
    skipStep: {
      condition: (flowValues) =>
        params.platform === 'web' || conditionSchema.safeParse(flowValues).success,
      targetStepName: 'login',
    },
    subFlow: SubFlow.ChildSaving,
    hideBackButton: true,
    buttonLabel: {
      type: 'translatableText',
      translationKey: 'sharedFlows_childSaving:steps.quote.proceedButtonLabel',
    },
  } as const satisfies AnyStepConfiguration
}
